import { useCallback, useRef } from "react";

/** Class that `modern-theme.css` styles in place of `:has(> div > .gm-pc)`. */
export const PC_SCREEN_CLASS = "gm-pc-screen";

/**
 * Product-config screens (`.gm-pc`) need their wrapper's parent to be a full-height flex column.
 * The theme used to find that parent with `:has(> div > .gm-pc) > div`, which makes every DOM insert
 * on the page recompute styles. This marks the parent explicitly instead.
 *
 * Attach the returned ref to the element that directly wraps `.gm-pc`. While `active` is true the
 * wrapper's parent carries `PC_SCREEN_CLASS`; it is removed when `active` flips or the wrapper
 * unmounts. The ref callback runs in the commit phase, so the class changes in the same paint as
 * `.gm-pc` appears or disappears. The parent must not set its own `className` prop (React would
 * overwrite the class); none of the current parents do.
 */
export const usePcScreenMarker = (active: boolean) => {
  const markedRef = useRef<HTMLElement | null>(null);

  return useCallback(
    (wrapper: HTMLElement | null) => {
      markedRef.current?.classList.remove(PC_SCREEN_CLASS);
      markedRef.current = null;

      const parent = active ? wrapper?.parentElement : null;
      if (parent) {
        parent.classList.add(PC_SCREEN_CLASS);
        markedRef.current = parent;
      }
    },
    [active]
  );
};
